<template>
    <div class="login-shell">
        <div class="login-card">
            <div class="brand-block">
                <div class="brand-mark">
                    <i class="bi bi-box" style="font-size:28px" aria-hidden="true"></i>
                </div>
                <div class="brand-meta">
                    <span class="brand-name">Indexer</span>
                    <span class="brand-env" :class="'env-' + envBadge.tone">{{ envBadge.label }}</span>
                </div>
            </div>

            <h1 class="login-title">Sign in</h1>
            <p class="login-subtitle">Access the Influtool indexer panel.</p>

            <form @submit.prevent="login" novalidate>
                <div class="field">
                    <label for="login-username">Username</label>
                    <input
                        id="login-username"
                        v-model="username"
                        type="text"
                        autocomplete="username"
                        autofocus
                        :disabled="loading"
                        placeholder="your.username"
                    >
                </div>

                <div class="field">
                    <label for="login-password">Password</label>
                    <div class="password-wrap">
                        <input
                            id="login-password"
                            v-model="password"
                            :type="showPassword ? 'text' : 'password'"
                            autocomplete="current-password"
                            :disabled="loading"
                            placeholder="••••••••"
                        >
                        <button
                            type="button"
                            class="password-toggle"
                            @click="showPassword = !showPassword"
                            :aria-label="showPassword ? 'Hide password' : 'Show password'"
                            tabindex="-1"
                        >
                            <i class="bi bi-eye" v-if="!showPassword" style="font-size:18px" aria-hidden="true"></i>
                            <i class="bi bi-eye-slash" v-else style="font-size:18px" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>

                <button type="submit" class="submit-btn" :disabled="loading || !username || !password">
                    <span v-if="!loading">Sign in</span>
                    <span v-else class="spinner" aria-hidden="true"></span>
                </button>
            </form>

            <div class="api-hint">
                <span class="api-dot"></span>
                <span>{{ apiHostLabel }}</span>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
      name: "login",
      data() {
        return {
          username: '',
          password: '',
          showPassword: false,
          loading: false,
          apiHost: this.$appSettings.getSetting('host')
        }
      },
      computed: {
        envBadge() {
          const h = (typeof location !== 'undefined' ? location.hostname : '') || '';
          if (h.indexOf('cz.influtool') !== -1) return { label: 'CZ', tone: 'cz' };
          if (h.indexOf('pl.influtool') !== -1) return { label: 'PL', tone: 'pl' };
          if (h.indexOf('anystar') !== -1) return { label: 'Anystar', tone: 'anystar' };
          if (h.indexOf('influtool.ovh') !== -1) return { label: 'DEV', tone: 'dev' };
          if (h === 'localhost' || h === '127.0.0.1' || h.indexOf('0.0.0.0') !== -1) return { label: 'LOCAL', tone: 'local' };
          return { label: 'PROD', tone: 'prod' };
        },
        apiHostLabel() {
          try {
            return new URL(this.apiHost).host;
          } catch (e) {
            return this.apiHost;
          }
        }
      },
      methods: {
        login() {
          if (this.loading || !this.username || !this.password) return;
          this.loading = true;

          this.$http.post(this.apiHost + "login_check", {
            username: this.username,
            password: this.password
          }, {
            emulateJSON: true
          }).then(response => {
            if (response.status === 200) {
              this.$auth.setToken(response.body.token);
              this.$router.push('/influencers');
            }
          }, response => {
            const message = (response && response.body && response.body.message)
              ? response.body.message
              : 'Invalid username or password.';
            this.$notify({ group: 'default', type: 'error', title: 'Login failed', text: message });
          }).finally(() => {
            this.loading = false;
          });
        }
      }
    }
</script>

<style scoped>
.login-shell {
    min-height: calc(100vh - 80px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
}

.login-card {
    width: 100%;
    max-width: 420px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 40px 36px 32px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
}

.brand-block {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 28px;
}
.brand-mark {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, #265a88, #1f4a70);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 14px rgba(38, 90, 136, 0.25);
}
.brand-meta {
    display: flex;
    align-items: center;
    gap: 8px;
}
.brand-name {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.01em;
}
.brand-env {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 999px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}
.env-prod    { color: #03543f; background: #def7ec; }
.env-dev     { color: #92400e; background: #fef3c7; }
.env-local   { color: #1f2937; background: #f3f4f6; }
.env-cz      { color: #1e40af; background: #dbeafe; }
.env-pl      { color: #9b1c1c; background: #fde8e8; }
.env-anystar { color: #5b21b6; background: #ede9fe; }

.login-title {
    font-size: 24px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 6px;
    letter-spacing: -0.01em;
}
.login-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 28px;
}

.field {
    margin-bottom: 18px;
}
.field label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #334155;
    margin-bottom: 6px;
}
.field input {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    background: #ffffff;
    color: #0f172a;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
}
.field input::placeholder {
    color: #64748b;
}
.field input:focus {
    outline: none;
    border-color: #265a88;
    box-shadow: 0 0 0 3px rgba(38, 90, 136, 0.15);
}
.field input:disabled {
    background: #f9fafb;
    color: #64748b;
    cursor: not-allowed;
}

.password-wrap {
    position: relative;
}
.password-wrap input {
    padding-right: 44px;
}
.password-toggle {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s, background 0.15s;
}
.password-toggle:hover {
    color: #265a88;
    background: #f1f5f9;
}

.submit-btn {
    width: 100%;
    height: 46px;
    margin-top: 8px;
    border: none;
    border-radius: 10px;
    background: #265a88;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, transform 0.05s;
}
.submit-btn:hover:not(:disabled) {
    background: #1f4a70;
}
.submit-btn:active:not(:disabled) {
    transform: translateY(1px);
}
.submit-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
}

.spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}
@keyframes spin {
    to { transform: rotate(360deg); }
}

.api-hint {
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #64748b;
}
.api-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}
</style>



// WEBPACK FOOTER //
// src/components/authentication/Login.vue